import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import SEO from "../components/SEO";
import "../home.css";
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  LogOut,
  Save,
  X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LoginForm({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      onLogin(res.data.token);
    } catch {
      setError("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--sky)", fontFamily: "'DM Sans', sans-serif" }}>
      <div className="w-full max-w-sm" data-testid="admin-login-form">
        <div className="text-center mb-8">
          <Link to="/" className="hm-display text-xl tracking-tight" style={{ color: "var(--ink)", textDecoration: "none" }}>
            Tulsi <span style={{ color: "var(--green)" }}>Giri</span>
          </Link>
          <h1 className="hm-display mt-6" style={{ fontSize: "1.6rem", fontWeight: 700, color: "var(--ink)" }}>
            Admin
          </h1>
          <p style={{ color: "var(--ink3)", fontSize: "0.9rem", marginTop: 4 }}>
            Enter password to manage blog posts
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "0.9rem", padding: "12px 16px", border: "1px solid var(--div)", borderRadius: 12, width: "100%", background: "var(--white)", color: "var(--ink)", outline: "none" }}
            data-testid="admin-password-input"
            autoFocus
          />
          {error && (
            <p style={{ color: "var(--coral)", fontSize: "0.85rem" }} data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading || !password}
            className="hm-btn-filled"
            style={{ width: "100%", justifyContent: "center", opacity: loading || !password ? 0.5 : 1 }}
            data-testid="admin-login-btn"
          >
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>
        <Link
          to="/"
          className="block text-center mt-6"
          style={{ fontSize: "0.85rem", color: "var(--ink3)", textDecoration: "none" }}
          data-testid="admin-back-link"
        >
          Back to site
        </Link>
      </div>
    </div>
  );
}

const emptyPost = {
  title: "",
  excerpt: "",
  content: "",
  category: "reflections",
  image_url: "",
  published: true,
};

const inputStyle = { fontFamily: "'DM Sans', sans-serif", fontSize: "0.9rem", padding: "10px 14px", border: "1px solid var(--div)", borderRadius: 10, width: "100%", background: "var(--white)", color: "var(--ink)", outline: "none" };

function PostEditor({ post, onSave, onCancel, saving }) {
  const [form, setForm] = useState(post || emptyPost);

  const update = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  return (
    <div style={{ background: "var(--white)", border: "1px solid var(--div)", borderRadius: 12, padding: 24 }} className="space-y-4" data-testid="post-editor">
      <div className="flex items-center justify-between mb-2">
        <h3 className="hm-display" style={{ fontSize: "1.1rem", fontWeight: 600, color: "var(--ink)" }}>
          {post ? "Edit Post" : "New Post"}
        </h3>
        <button onClick={onCancel} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink3)" }} data-testid="editor-cancel-btn">
          <X size={20} />
        </button>
      </div>

      <input value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="Post title" style={inputStyle} data-testid="editor-title-input" />
      <input value={form.excerpt} onChange={(e) => update("excerpt", e.target.value)} placeholder="Short excerpt / summary" style={inputStyle} data-testid="editor-excerpt-input" />
      <textarea value={form.content} onChange={(e) => update("content", e.target.value)} placeholder="Full content... (use ## for headings, > for quotes)" rows={10} style={{ ...inputStyle, resize: "vertical" }} data-testid="editor-content-input" />

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="hm-mono block mb-1" style={{ fontSize: "0.6rem", color: "var(--ink3)", letterSpacing: "0.12em", textTransform: "uppercase" }}>Category</label>
          <select value={form.category} onChange={(e) => update("category", e.target.value)} style={{ ...inputStyle, cursor: "pointer" }} data-testid="editor-category-select">
            <option value="reflections">Reflections</option>
            <option value="field-notes">Field Notes</option>
            <option value="policy">Policy</option>
            <option value="initiative-updates">Initiative Updates</option>
          </select>
        </div>
        <div>
          <label className="hm-mono block mb-1" style={{ fontSize: "0.6rem", color: "var(--ink3)", letterSpacing: "0.12em", textTransform: "uppercase" }}>Image URL</label>
          <input value={form.image_url} onChange={(e) => update("image_url", e.target.value)} placeholder="https://..." style={inputStyle} data-testid="editor-image-input" />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={() => update("published", !form.published)}
          className="hm-tag"
          style={{ background: form.published ? "var(--green-l)" : "var(--coral-l)", color: form.published ? "var(--green)" : "var(--coral)", cursor: "pointer", padding: "5px 14px", border: "none" }}
          data-testid="editor-publish-toggle"
        >
          {form.published ? <Eye size={12} /> : <EyeOff size={12} />}
          <span style={{ marginLeft: 6 }}>{form.published ? "Published" : "Draft"}</span>
        </button>
      </div>

      <button
        onClick={() => onSave(form)}
        disabled={saving || !form.title || !form.content}
        className="hm-btn-filled"
        style={{ width: "100%", justifyContent: "center", opacity: saving || !form.title || !form.content ? 0.5 : 1 }}
        data-testid="editor-save-btn"
      >
        <Save size={16} />
        {saving ? "Saving..." : "Save Post"}
      </button>
    </div>
  );
}

const catColors = {
  reflections: { bg: "var(--green-l)", color: "var(--green)" },
  "field-notes": { bg: "var(--coral-l)", color: "var(--coral)" },
  policy: { bg: "var(--blue-l)", color: "var(--blue)" },
  "initiative-updates": { bg: "var(--amber-l)", color: "var(--amber)" },
};

function PostList({ posts, onEdit, deleteConfirm, setDeleteConfirm, onDelete, editing, onNewPost }) {
  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <h1 className="hm-display" style={{ fontSize: "1.4rem", fontWeight: 600, color: "var(--ink)" }}>
          Manage Posts ({posts.length})
        </h1>
        {!editing && (
          <button onClick={onNewPost} className="hm-btn-filled" data-testid="admin-new-post-btn">
            <Plus size={16} /> New Post
          </button>
        )}
      </div>

      <div className="space-y-3">
        {posts.map((post) => (
          <div
            key={post.id}
            style={{ background: "var(--white)", border: "1px solid var(--div)", borderRadius: 12, padding: 20 }}
            className="flex items-start justify-between gap-4"
            data-testid={`admin-post-${post.id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-medium truncate" style={{ color: "var(--ink)" }}>{post.title}</h3>
                <span className="hm-tag flex-shrink-0" style={{ background: post.published ? "var(--green-l)" : "var(--coral-l)", color: post.published ? "var(--green)" : "var(--coral)" }}>
                  {post.published ? "Published" : "Draft"}
                </span>
              </div>
              <p className="truncate" style={{ color: "var(--ink3)", fontSize: "0.85rem" }}>{post.excerpt}</p>
              <div className="flex items-center gap-2 mt-1">
                {post.category && (
                  <span className="hm-tag" style={{ background: catColors[post.category]?.bg || "var(--sky)", color: catColors[post.category]?.color || "var(--ink3)" }}>
                    {post.category.replace("-", " ")}
                  </span>
                )}
                <span style={{ color: "var(--ink3)", fontSize: "0.75rem" }}>{new Date(post.created_at).toLocaleDateString()}</span>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={() => onEdit(post)} style={{ padding: 8, borderRadius: 8, background: "none", border: "none", cursor: "pointer", color: "var(--ink3)" }} data-testid={`edit-post-${post.id}`}>
                <Pencil size={16} />
              </button>
              {deleteConfirm === post.id ? (
                <div className="flex items-center gap-1">
                  <button onClick={() => onDelete(post.id)} style={{ padding: "4px 10px", background: "var(--coral)", color: "var(--white)", fontSize: "0.75rem", borderRadius: 8, border: "none", cursor: "pointer" }} data-testid={`confirm-delete-${post.id}`}>Delete</button>
                  <button onClick={() => setDeleteConfirm(null)} style={{ padding: "4px 10px", background: "var(--div)", color: "var(--ink2)", fontSize: "0.75rem", borderRadius: 8, border: "none", cursor: "pointer" }} data-testid={`cancel-delete-${post.id}`}>Cancel</button>
                </div>
              ) : (
                <button onClick={() => setDeleteConfirm(post.id)} style={{ padding: 8, borderRadius: 8, background: "none", border: "none", cursor: "pointer", color: "var(--ink3)" }} data-testid={`delete-post-${post.id}`}>
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          </div>
        ))}

        {posts.length === 0 && !editing && (
          <div className="text-center py-16" style={{ color: "var(--ink3)" }}>
            <p className="mb-4">No posts yet.</p>
            <button onClick={onNewPost} style={{ color: "var(--green)", fontWeight: 600, background: "none", border: "none", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }} data-testid="admin-empty-new-btn">
              Create your first post
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export default function Admin() {
  const [token, setToken] = useState("");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleLogout = useCallback(() => {
    setToken("");
    setPosts([]);
  }, []);

  const fetchPosts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/blog`, { headers: { Authorization: `Bearer ${token}` } });
      setPosts(res.data);
    } catch (e) {
      if (e.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  }, [token, handleLogout]);

  useEffect(() => {
    if (token) fetchPosts();
    else setLoading(false);
  }, [token, fetchPosts]);

  const handleLogin = useCallback((t) => { setToken(t); }, []);

  const handleSave = useCallback(async (formData) => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editing && editing !== "new" && editing.id) {
        await axios.put(`${API}/blog/${editing.id}`, formData, { headers });
      } else {
        await axios.post(`${API}/blog`, formData, { headers });
      }
      setEditing(null);
      await fetchPosts();
    } catch {
      /* save failed silently */
    } finally {
      setSaving(false);
    }
  }, [token, editing, fetchPosts]);

  const handleDelete = useCallback(async (postId) => {
    try {
      await axios.delete(`${API}/blog/${postId}`, { headers: { Authorization: `Bearer ${token}` } });
      setDeleteConfirm(null);
      await fetchPosts();
    } catch {
      /* delete failed silently */
    }
  }, [token, fetchPosts]);

  if (!token) return (
    <>
      <SEO title="Admin — Tulsi Giri" description="" path="/admin">
        <meta name="robots" content="noindex,nofollow" />
      </SEO>
      <LoginForm onLogin={handleLogin} />
    </>
  );

  return (
    <>
      <SEO title="Admin — Tulsi Giri" description="" path="/admin">
        <meta name="robots" content="noindex,nofollow" />
      </SEO>

      <div className="min-h-screen" style={{ background: "var(--sky)", fontFamily: "'DM Sans', sans-serif" }} data-testid="admin-dashboard">
        <header className="sticky top-0 z-50" style={{ background: "var(--ink)", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
          <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" style={{ color: "rgba(255,255,255,0.6)", textDecoration: "none" }} data-testid="admin-home-link"><ArrowLeft size={18} /></Link>
              <span className="hm-display" style={{ color: "var(--white)", fontWeight: 600 }}>Blog Admin</span>
            </div>
            <button onClick={handleLogout} className="inline-flex items-center gap-2" style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.85rem", background: "none", border: "none", cursor: "pointer" }} data-testid="admin-logout-btn">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </header>

        <div className="max-w-5xl mx-auto px-6 py-8">
          {editing && (
            <div className="mb-6">
              <PostEditor post={editing === "new" ? null : editing} onSave={handleSave} onCancel={() => setEditing(null)} saving={saving} />
            </div>
          )}

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} style={{ background: "var(--white)", borderRadius: 12, padding: 20, border: "1px solid var(--div)" }}>
                  <div style={{ height: 20, background: "var(--div)", borderRadius: 6, width: "66%", marginBottom: 8 }} />
                  <div style={{ height: 16, background: "var(--div)", borderRadius: 6, width: "33%" }} />
                </div>
              ))}
            </div>
          ) : (
            <PostList
              posts={posts}
              onEdit={setEditing}
              deleteConfirm={deleteConfirm}
              setDeleteConfirm={setDeleteConfirm}
              onDelete={handleDelete}
              editing={editing}
              onNewPost={() => setEditing("new")}
            />
          )}
        </div>
      </div>
    </>
  );
}
